import { useCallback, useEffect, useMemo, useState } from "react";
import type { BoardSnapshot, CreateWorkItem, UpdateWorkItem, WorkItem } from "@business-ops/domain";
import { api, ApiError } from "./api.js";

export interface BoardController {
  board: BoardSnapshot | null;
  loading: boolean;
  refresh: () => Promise<void>;
  createWork: (input: CreateWorkItem) => Promise<WorkItem>;
  updateWork: (item: WorkItem, input: Omit<UpdateWorkItem, "expectedVersion">) => Promise<WorkItem>;
}

type BoardEventSource = Pick<EventSource, "addEventListener" | "removeEventListener" | "close">;

export type BoardRefreshHint =
  | { kind: "READY"; workspaceId: string }
  | { kind: "BOARD_CHANGE"; workspaceId: string; event: string; entityId: string };

type BoardRefreshHintListener = (hint: BoardRefreshHint) => void;
const boardRefreshHintListeners = new Set<BoardRefreshHintListener>();

export function subscribeBoardRefreshHints(listener: BoardRefreshHintListener): () => void {
  boardRefreshHintListeners.add(listener);
  return () => { boardRefreshHintListeners.delete(listener); };
}

function boardRefreshHintFromEvent(event: Event): BoardRefreshHint | null {
  if (event.type !== "ready" && event.type !== "board-change") return null;
  const data = (event as Event & { data?: unknown }).data;
  if (typeof data !== "string") return null;
  try {
    const parsed = JSON.parse(data) as { workspaceId?: unknown; event?: unknown; entityId?: unknown };
    if (typeof parsed.workspaceId !== "string") return null;
    if (event.type === "ready") return { kind: "READY", workspaceId: parsed.workspaceId };
    if (typeof parsed.event !== "string" || typeof parsed.entityId !== "string") return null;
    return {
      kind: "BOARD_CHANGE",
      workspaceId: parsed.workspaceId,
      event: parsed.event,
      entityId: parsed.entityId
    };
  } catch {
    return null;
  }
}

function publishBoardRefreshHint(event: Event): void {
  const hint = boardRefreshHintFromEvent(event);
  if (!hint) return;
  for (const listener of [...boardRefreshHintListeners]) {
    try { listener(hint); } catch { /* One mounted consumer cannot block the canonical refresh. */ }
  }
}

/**
 * WHY [BOL-BOARD-REFRESH-TRAILING-001]: a database event can arrive while the
 * previous Board read is still in flight. Returning only that old promise loses
 * the newer event and leaves Scheduled/Sent state stale until a page reload.
 * Coalesce bursts, but always run one trailing canonical read. Falsifier: two
 * refresh requests during one read produce only the pre-event snapshot.
 */
export function createTrailingRefresh(operation: () => Promise<void>): () => Promise<void> {
  let pending = false;
  let inFlight: Promise<void> | null = null;
  return () => {
    pending = true;
    if (inFlight) return inFlight;
    const drain = async () => {
      while (pending) {
        pending = false;
        await operation();
      }
    };
    inFlight = drain().finally(() => { inFlight = null; });
    return inFlight;
  };
}

/**
 * WHY [BOL-BOARD-SSE-REANCHOR-001]: EventSource reconnects after a dropped
 * stream, but events emitted while disconnected are not replayed by this
 * snapshot-based endpoint. The server's authenticated `ready` event therefore
 * re-anchors Board/Review to PostgreSQL; listening only for board-change can
 * preserve stale status forever. Falsifier: reconnect requires a page reload.
 */
export function connectBoardEvents(events: BoardEventSource, refresh: EventListener): () => void {
  // WHY [BOL-MOUNTED-PROPOSAL-SSE-001]: this authenticated EventSource is the
  // canonical change hint for both Board and Review. Reuse its content-free,
  // workspace-scoped identity for mounted Chat consumers; another EventSource
  // or timer would multiply connections while still not own proposal truth.
  // The hint authorizes only a matching exact GET. Falsifier: a scheduled
  // proposal terminalizes after the bounded waiter and Chat needs a page reload.
  const receive: EventListener = (event) => {
    refresh(event);
    publishBoardRefreshHint(event);
  };
  events.addEventListener("ready", receive);
  events.addEventListener("board-change", receive);
  return () => {
    events.removeEventListener("ready", receive);
    events.removeEventListener("board-change", receive);
    events.close();
  };
}

export function useBoard(onError: (message: string) => void, onRealtimeChange?: () => void): BoardController {
  const [board, setBoard] = useState<BoardSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = useMemo(() => createTrailingRefresh(async () => {
    try {
      setBoard(await api.board());
    } catch (error) {
      onError(error instanceof ApiError ? error.message : "The board could not be loaded.");
    } finally {
      setLoading(false);
    }
  }), [onError]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    const events = new EventSource("/api/events");
    let timer: number | undefined;
    const scheduleRefresh = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void refresh();
        onRealtimeChange?.();
      }, 120);
    };
    const disconnect = connectBoardEvents(events, scheduleRefresh);
    return () => {
      window.clearTimeout(timer);
      disconnect();
    };
  }, [onRealtimeChange, refresh]);

  const createWork = useCallback(async (input: CreateWorkItem) => {
    const created = await api.createWork(input);
    setBoard((current) => current ? { ...current, workItems: [...current.workItems, created] } : current);
    return created;
  }, []);

  const updateWork = useCallback(async (item: WorkItem, input: Omit<UpdateWorkItem, "expectedVersion">) => {
    try {
      const updated = await api.updateWork(item.id, { ...input, expectedVersion: item.version });
      setBoard((current) => current ? {
        ...current,
        workItems: current.workItems.map((candidate) => candidate.id === updated.id ? updated : candidate)
      } : current);
      return updated;
    } catch (error) {
      if (error instanceof ApiError && error.code === "VERSION_CONFLICT") await refresh();
      throw error;
    }
  }, [refresh]);

  return { board, loading, refresh, createWork, updateWork };
}
